import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Save, Building2, Landmark } from "lucide-react";
import { toast } from "sonner";

const DEFAULT = {
  company_name: "D4PS Couriers & Logistics Ltd",
  company_number: "16266460",
  vat_number: "",
  address_line1: "Globe House Business Centre, Office F3",
  address_line2: "Globe Park, Kingsway West, Moss Bridge Road",
  city: "Rochdale",
  postcode: "OL16 5EB",
  country: "United Kingdom",
  email: "info@d4pslogistics.com",
  phone: "07860198559",
  bank_name: "",
  bank_account_name: "",
  bank_sort_code: "",
  bank_account_number: "",
};

export default function Settings() {
  const [s, setS] = useState(DEFAULT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setS({ ...DEFAULT, ...r.data })).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", s);
      toast.success("Company settings saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const set = (k, v) => setS((prev) => ({ ...prev, [k]: v }));

  return (
    <div data-testid="settings-page" className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Configuration</div>
          <h1 className="font-display text-3xl tracking-tight font-semibold text-slate-900 mt-1">Settings</h1>
          <p className="text-slate-600 mt-2 text-sm">
            Company info shown on PDFs. Bank details appear on Self-Bills.
          </p>
        </div>
        <Button data-testid="settings-save" onClick={save} disabled={saving || loading} className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white">
          <Save className="h-4 w-4 mr-2" /> Save changes
        </Button>
      </div>

      <Card className="rounded-sm border border-slate-200 shadow-none p-6">
        <SectionHeading icon={Building2} title="Company details" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <Field label="Company name" k="company_name" s={s} set={set} />
          <Field label="Company number" k="company_number" s={s} set={set} />
          <Field label="VAT number" k="vat_number" s={s} set={set} />
          <Field label="Email" k="email" s={s} set={set} />
          <Field label="Phone" k="phone" s={s} set={set} />
          <Field label="Country" k="country" s={s} set={set} />
          <Field label="Address line 1" k="address_line1" s={s} set={set} />
          <Field label="Address line 2" k="address_line2" s={s} set={set} />
          <Field label="City" k="city" s={s} set={set} />
          <Field label="Postcode" k="postcode" s={s} set={set} />
        </div>
      </Card>

      <Card className="rounded-sm border border-slate-200 shadow-none p-6">
        <SectionHeading icon={Landmark} title="Bank details (for Self-Bills)" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <Field label="Bank name" k="bank_name" s={s} set={set} />
          <Field label="Account name" k="bank_account_name" s={s} set={set} />
          <Field label="Sort code" k="bank_sort_code" s={s} set={set} />
          <Field label="Account number" k="bank_account_number" s={s} set={set} />
        </div>
      </Card>
    </div>
  );
}

function SectionHeading({ icon: Icon, title }) {
  return (
    <div className="flex items-center gap-3 pb-3 border-b border-slate-100">
      <div className="h-8 w-8 rounded-sm bg-slate-900 text-white flex items-center justify-center">
        <Icon className="h-4 w-4" />
      </div>
      <h2 className="font-display text-lg font-medium text-slate-900">{title}</h2>
    </div>
  );
}

function Field({ label, k, s, set }) {
  const testid = `settings-${k.replace(/_/g, "-")}`;
  return (
    <div>
      <Label className="text-xs text-slate-600">{label}</Label>
      <Input data-testid={testid} value={s[k] || ""} onChange={(e) => set(k, e.target.value)}
        className="mt-1 rounded-sm border-slate-300" />
    </div>
  );
}
